import { type ReactNode, useMemo } from 'react';
import {
  AppBar,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import RuleIcon from '@mui/icons-material/Rule';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Wordmark } from './Wordmark';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { CredentialExpiredBanner } from './CredentialExpiredBanner';
import type { Me } from '@/api/types';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

const RAIL_WIDTH = 220;

function buildNav(isAdmin: boolean): NavItem[] {
  const base: NavItem[] = [
    { to: '/connections', label: 'Connections', icon: <LinkIcon /> },
    { to: '/rules', label: 'Rules', icon: <RuleIcon /> },
  ];
  if (isAdmin) base.push({ to: '/admin', label: 'Admin', icon: <AdminPanelSettingsIcon /> });
  return base;
}

export function Layout({ me, children }: { me: Me; children: ReactNode }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const nav = useMemo(() => buildNav(me.is_admin), [me.is_admin]);
  const location = useLocation();
  const navigate = useNavigate();

  // Mark the matching bottom-nav tab by longest-prefix match so e.g.
  // /rules/42 still highlights "Rules".
  const activeIndex = nav.findIndex((n) =>
    location.pathname === n.to || location.pathname.startsWith(`${n.to}/`),
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      {isDesktop && (
        <Drawer
          variant="permanent"
          sx={{
            width: RAIL_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': { width: RAIL_WIDTH, boxSizing: 'border-box' },
          }}
        >
          <Toolbar sx={{ px: 2 }}>
            <Wordmark />
          </Toolbar>
          <List sx={{ px: 1 }}>
            {nav.map((item) => (
              <ListItemButton
                key={item.to}
                component={NavLink}
                to={item.to}
                sx={{
                  borderRadius: 1.5,
                  mb: 0.5,
                  '&.active': {
                    bgcolor: 'action.selected',
                    color: 'primary.main',
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="sticky">
          <Toolbar sx={{ gap: 1 }}>
            {!isDesktop && <Wordmark />}
            <Box sx={{ flexGrow: 1 }} />
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ThemeToggle />
              <UserMenu me={me} />
            </Stack>
          </Toolbar>
        </AppBar>

        <CredentialExpiredBanner />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            px: { xs: 2, md: 4 },
            py: { xs: 2, md: 3 },
            pb: { xs: 10, md: 4 },
            maxWidth: 1200,
            width: '100%',
            mx: 'auto',
            minWidth: 0,
          }}
        >
          {children}
        </Box>

        {!isDesktop && (
          <BottomNavigation
            value={activeIndex === -1 ? 0 : activeIndex}
            onChange={(_, idx) => navigate(nav[idx].to)}
            showLabels
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              borderTop: 1,
              borderColor: 'divider',
              zIndex: (t) => t.zIndex.appBar,
            }}
          >
            {nav.map((item) => (
              <BottomNavigationAction key={item.to} label={item.label} icon={item.icon} />
            ))}
          </BottomNavigation>
        )}
      </Box>
    </Box>
  );
}
